import axios from "axios";

export const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  timeout: 20000,
  withCredentials: true,
});

export const submitContact = (payload) => api.post("/contact", payload).then((r) => r.data);

export const apiErrorMessage = (err) => {
  if (err?.response?.status === 429) return err.response.data?.detail || "Too many attempts. Please try again in a few minutes.";
  const detail = err?.response?.data?.detail;
  if (Array.isArray(detail)) return (detail[0]?.msg || "Please check the form and try again.").replace(/^Value error, /, "");
  return typeof detail === "string" ? detail : "Something went wrong. Please try again.";
};

const data = (p) => p.then((r) => r.data);

export const ownerApi = {
  login: (body) => data(api.post("/owner/auth/login", body)),
  setupPassword: (body) => data(api.post("/owner/auth/setup-password", body)),
  changePassword: (body) => data(api.post("/owner/auth/password", body)),
  register: (body) => data(api.post("/owner/auth/register", body)),
  logout: () => data(api.post("/owner/auth/logout")),
  me: () => data(api.get("/owner/me")),
  updateMe: (body) => data(api.patch("/owner/me", body)),
  requestMobileChange: (mobile) => data(api.post("/owner/me/mobile/request-otp", { mobile })),
  verifyMobileChange: (mobile, code) => data(api.post("/owner/me/mobile/verify", { mobile, code })),
  deactivate: () => data(api.post("/owner/me/deactivate", { confirm: "DEACTIVATE" })),
  dashboard: () => data(api.get("/owner/dashboard")),
  vehicles: () => data(api.get("/owner/vehicles")),
  vehicle: (id) => data(api.get(`/owner/vehicles/${id}`)),
  addVehicle: (body) => data(api.post("/owner/vehicles", body)),
  updateVehicle: (id, body) => data(api.patch(`/owner/vehicles/${id}`, body)),
  photoBlob: (id) => data(api.get(`/owner/vehicles/${id}/photo`, { responseType: "blob" })),
  uploadPhoto: (id, file) => data(api.put(`/owner/vehicles/${id}/photo`, file, { headers: { "Content-Type": file.type } })),
  deletePhoto: (id) => data(api.delete(`/owner/vehicles/${id}/photo`)),
  photoSettings: (id, body) => data(api.put(`/owner/vehicles/${id}/photo-settings`, body)),
  lookupVehicle: (registration_number) => data(api.post("/owner/vehicles/lookup", { registration_number })),
  slotBlob: (id, slot) => data(api.get(`/owner/vehicles/${id}/photos/${slot}`, { responseType: "blob" })),
  uploadSlot: (id, slot, blob) => data(api.put(`/owner/vehicles/${id}/photos/${slot}`, blob, { headers: { "Content-Type": "image/jpeg" } })),
  deleteSlot: (id, slot) => data(api.delete(`/owner/vehicles/${id}/photos/${slot}`)),
  primarySlot: (id, slot) => data(api.put(`/owner/vehicles/${id}/photos/${slot}/primary`)),
  setAway: (id, away) => data(api.put(`/owner/vehicles/${id}/away`, { away })),
  alertOptions: () => data(api.get("/owner/alerts/quick-replies")),
  alertLocation: (id, body) => data(body ? api.post(`/owner/alerts/${id}/location`, body) : api.delete(`/owner/alerts/${id}/location`)),
  qrCodes: () => data(api.get("/owner/qr-codes")),
  qrCode: (serial) => data(api.get(`/owner/qr-codes/${serial}`)),
  qrBlob: (serial, fmt) => data(api.get(`/owner/qr-codes/${serial}/download/${fmt}`, { responseType: "blob" })),
  alerts: () => data(api.get("/owner/alerts")),
  notifications: () => data(api.get("/owner/settings/notifications")),
  saveNotifications: (body) => data(api.put("/owner/settings/notifications", body)),
  contacts: () => data(api.get("/owner/settings/emergency-contacts")),
  addContact: (body) => data(api.post("/owner/settings/emergency-contacts", body)),
  updateContact: (id, body) => data(api.put(`/owner/settings/emergency-contacts/${id}`, body)),
  removeContact: (id) => data(api.delete(`/owner/settings/emergency-contacts/${id}`)),
};

Object.assign(ownerApi, {
  alertList: (scope = "all") => data(api.get(`/owner/alerts?scope=${scope}`)),
  alertSummary: () => data(api.get("/owner/alerts/summary")),
  alert: (id) => data(api.get(`/owner/alerts/${id}`)),
  alertAction: (id, action, body) => data(api.post(`/owner/alerts/${id}/${action}`, body)),
});

const scanHeaders = (serial) => ({ headers: { "X-Scanner-Session": localStorage.getItem(`carlink_scan_${serial}`) || "" } });

export const scanApi = {
  view: (serial) => data(api.get(`/public/v/${serial}`, scanHeaders(serial))),
  send: (serial, body) => data(api.post(`/public/v/${serial}/alerts`, body, scanHeaders(serial))),
  report: (serial, ref) => data(api.get(`/public/v/${serial}/reports/${ref}`, scanHeaders(serial))),
  reply: (serial, ref, body) => data(api.post(`/public/v/${serial}/reports/${ref}/messages`, { body }, scanHeaders(serial))),
  call: (serial, ref) => data(api.post(`/public/v/${serial}/reports/${ref}/call`, {}, scanHeaders(serial))),
  location: (serial, ref, body) => data(api.post(`/public/v/${serial}/reports/${ref}/location`, body, scanHeaders(serial))),
  uploadPhoto: (serial, ref, blob) => data(api.post(`/public/v/${serial}/reports/${ref}/photos`, blob, { headers: { ...scanHeaders(serial).headers, "Content-Type": "image/jpeg" } })),
  sharedLocation: (token) => data(api.get(`/public/loc/${token}`)),
};

const FILE_NAMES = { png: (s) => `CARLINK-${s}.png`, pdf: (s) => `CARLINK-${s}.pdf`, sticker: (s) => `CARLINK-${s}-sticker.png` };

export const downloadQr = async (serial, fmt) => {
  const blob = await ownerApi.qrBlob(serial, fmt);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = FILE_NAMES[fmt](serial);
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
};
